import type { PricingCampaignModel } from '@/shared/generated'

import { useMemo } from 'react'
import { entries, fromPairs, indexBy, map, pipe, prop } from 'lodash/fp'
import { useDebounceValue } from 'usehooks-ts'

import {
	alignPricingCampaignsOrders,
	isValidMatchedProductsRequest,
	isValidMatchedProductsRequestItem,
	useMatchedProductsQuery,
} from '@/entities/pricing-campaign'

/**
 * Hook to get matched products counts for pricing campaigns with product assignments
 * @param pricingCampaigns - Full list of pricing campaigns
 * @param optimizationGroupId - Optimization group id
 * @returns - Result query with Object with pricing campaign id as key and number of products as value
 */
export const useOrderedPricingCampaignsProductsCounts = (
	pricingCampaigns: PricingCampaignModel[],
	scenarioId?: string | null
) => {
	const [items] = useDebounceValue(pricingCampaigns, 1000)
	const assignments = useMemo(
		() =>
			alignPricingCampaignsOrders(items)
				.map((pc) => ({
					id: pc.id,
					order: pc.order,
					assignments: pc.settings.product_assignments.map(
						({ name, filters }) => ({
							id: name,
							filters,
						})
					),
				}))
				.filter(isValidMatchedProductsRequestItem),
		[items]
	)

	return useMatchedProductsQuery<Record<string, number>>(
		{
			scenario_id: scenarioId ?? null,
			pricing_campaigns: assignments,
		},
		{
			enabled: isValidMatchedProductsRequest(assignments),
			select: pipe([
				indexBy(prop('id')),
				entries,
				map(([id, { pricing_line_count }]) => [id, pricing_line_count]),
				fromPairs,
			]),
		}
	)
}
